import { useTerminalsContext } from "contexts/TerminalsProvider";
import React from "react";
import { SelectInput } from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { isInArray } from "utils/general";
import { getOrderServiceActiveChoices } from "utils/getter";

import {
	CoolerType,
	OrderService,
	perEligibleServices,
	thirdAssigneeEligibleServices,
} from "../../../../entities";

const getDedicatedTerminalId = (terminalId, terminals) => {
	if (terminalId != null) {
		return terminalId;
	}
	if (terminals.length === 0) {
		return null;
	}
	if (terminals.length === 1) {
		return terminals[0].id;
	}
	return terminals.map((t) => t.id);
};

export const ServiceInputRaw = ({
	isCouldStorageVisible = true,
	terminalId: terminalIdProp,
	...props
}) => {
	const { data: _terminals } = useTerminalsContext();
	const { values } = useFormState();
	const form = useForm();

	const terminalId = terminalIdProp ?? values.terminal_id;

	const OrderServiceActiveChoices = React.useMemo(() => {
		const terminals = Object.values(_terminals);
		const dedicateTerminalId = getDedicatedTerminalId(terminalId, terminals);
		return getOrderServiceActiveChoices(dedicateTerminalId, props.record.service, terminals);
	}, [terminalId, props.record.service, _terminals]);

	if (
		values.service != null &&
		!isInArray(
			OrderServiceActiveChoices.map((service) => service.id),
			values.service
		)
	) {
		form.change("service", null);
		form.change("is_per", false);
	}

	const choices = OrderServiceActiveChoices.filter((service) =>
		service.id === OrderService.coldStorage ? isCouldStorageVisible : true
	);

	return <SelectInput {...props} choices={choices} />;
};

const useServiceChange = (form, opts = {}) => {
	const resetColdStorageFields = () => {
		form.batch(() => {
			form.change("is_reefer", null);
			form.change("total_boxes", null);
			form.change("is_photo_of_label", null);
			form.change("container_number", null);
		});
	};

	const setCompanyColdStorageFields = () => {
		if (opts.company) {
			form.batch(() => {
				form.change("container_number", null);
				form.change("total_boxes", null);
				form.change("is_reefer", opts.company.cold_storage_is_reefer);
				form.change("cooler_type_id", opts.company.cold_storage_temperature_type);
				form.change("is_photo_of_label", opts.company.cold_storage_is_photo_of_label);
			});
		}
	};

	const handleServiceChange = (e) => {
		const service = e.target.value;

		switch (service) {
			case OrderService.reloadCarCar:
				form.change("cooler_type_id", CoolerType.other);
				resetColdStorageFields();
				break;
			case OrderService.reloadCarTerminalCar:
			case OrderService.intoPlukkStorage:
			case OrderService.crossDock:
			case OrderService.per:
				form.change("cooler_type_id", CoolerType.cooler);
				resetColdStorageFields();
				break;
			case OrderService.coldStorage:
				form.change("cooler_type_id", CoolerType.cooler);
				if (opts.withCompanyDefaults) {
					setCompanyColdStorageFields();
				}
				break;
			default:
				break;
		}

		if (!perEligibleServices.includes(service)) {
			form.change("is_per", false);
		}

		if (!thirdAssigneeEligibleServices.includes(service)) {
			form.change("assignee_3", null);
		}

		form.change("service", service);
	};

	return handleServiceChange;
};

export const ServiceInput = (props) => {
	const form = useForm();
	const handleServiceChange = useServiceChange(form);
	return <ServiceInputRaw {...props} onChange={handleServiceChange} />;
};

export const OrderManagerServiceInput = (props) => {
	const form = useForm();
	const handleServiceChange = useServiceChange(form, {
		company: props.company,
		withCompanyDefaults: true,
	});
	return <ServiceInputRaw {...props} onChange={handleServiceChange} />;
};
